import { DrawerToggleButton } from "@react-navigation/drawer";
import Drawer from "expo-router/drawer";
import { ActivityIndicator, RefreshControl, View, StyleSheet, Text, TouchableOpacity } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useCrew } from "@/context/CrewProvider";
import { trpc } from "@/trpc/react";
import { Card } from "@/components/ui/card";
import NotificationRenderer from "@/components/notification/NotificationRenderer";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import colors from '@/constants/Colors';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Ionicons } from '@expo/vector-icons';
import { useRef, useCallback } from 'react';

export default function Notifications() {
  const insets = useSafeAreaInsets();
  const { currentCrewMember } = useCrew();
  const childRefetchFunctions = useRef<(() => void)[]>([]);

  const { data: notifications, isLoading, refetch } = trpc.notification.list.useQuery({ crew_id: currentCrewMember!.id });

  // Register a child component's refetch function
  const registerRefetch = useCallback((refetchFn: () => void) => {
    childRefetchFunctions.current.push(refetchFn);
    return () => {
      const index = childRefetchFunctions.current.indexOf(refetchFn);
      if (index !== -1) {
        childRefetchFunctions.current.splice(index, 1);
      }
    };
  }, []);

  // Handle refresh - call parent and all child refetch functions
  const handleRefresh = useCallback(() => {
    refetch();
    childRefetchFunctions.current.forEach(fn => fn());
  }, [refetch]);

  // Refresh on focus the same way the refresh button does. The notification rows alone aren't
  // enough: each card reads its own accepted/declined state from getCrewListForJob, so refetching
  // only the parent leaves a card showing a state the office has already changed.
  useRefetchOnFocus(handleRefresh);

  if (isLoading) {
    return (
      <GestureHandlerRootView style={{ flex: 1 }}>
        <Drawer.Screen
          options={{
            drawerLabel: 'Job Requests',
            title: 'Job Requests',
            headerStyle: {
              backgroundColor: colors.primary,
            },
            headerTintColor: colors.accent,
            headerLeft: () => <DrawerToggleButton tintColor={colors.accent} />,
            headerRight: () => (
              <TouchableOpacity onPress={handleRefresh} style={styles.refreshButton}>
                <Ionicons name="refresh" size={24} color={colors.accent} />
              </TouchableOpacity>
            )
          }}
        />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#888888" />
        </View>
      </GestureHandlerRootView>
    )
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <Drawer.Screen
        options={{
          drawerLabel: 'Job Requests',
          title: 'Job Requests',
          headerStyle: {
            backgroundColor: colors.primary,
          },
          headerTintColor: colors.accent,
          headerLeft: () => <DrawerToggleButton tintColor={colors.accent} />,
          headerRight: () => (
            <TouchableOpacity onPress={handleRefresh} style={styles.refreshButton}>
              <Ionicons name="refresh" size={24} color={colors.accent} />
            </TouchableOpacity>
          )
        }}
      />
      <View style={styles.container}>
        <KeyboardAwareScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: 32 + insets.bottom }]}
          refreshControl={<RefreshControl refreshing={isLoading} onRefresh={handleRefresh} />}
        >
          {notifications?.length === 0 ? (
            <Card style={styles.emptyCard}>
              <View style={styles.headerContainer}>
                <View style={styles.iconContainer}>
                  <Ionicons name="briefcase-outline" size={24} color="white" />
                </View>
                <Text style={styles.headerTitle}>No Job Requests</Text>
              </View>
              <View style={styles.emptySection}>
                <Text style={styles.emptyText}>You have no new job requests.</Text>
                <Text style={styles.emptySubtext}>Tap refresh to check for new requests.</Text>
              </View>
            </Card>
          ) : notifications?.map((notification, index) => (
            <View key={index}>
              {notification && (
                <NotificationRenderer notification={notification} registerRefetch={registerRefetch} />
              )}
            </View>
          ))}
        </KeyboardAwareScrollView>
      </View>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f7',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
    backgroundColor: '#f5f5f7',
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  emptyCard: {
    marginTop: 16,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    backgroundColor: '#ffffff',
    borderWidth: 0,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  emptySection: {
    backgroundColor: '#f0f0f0',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e8e8e8',
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '500',
    color: '#000000',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#666666',
    marginTop: 8,
  },
  refreshButton: {
    marginRight: 16,
    padding: 4,
  },
});